import { CreditRedemptionViewModel } from '@mkp/shared/models';
import { CreditRedemptionUtils } from '@mkp/shared/util-product';
import { createSelector } from '@ngrx/store';
import { creditRedemptionFeature } from './credit-redemption.reducer';

export const { selectFetchState, selectEntities } = creditRedemptionFeature;

// Returns whichever active redeem the entity map yields first. "At most one active per vacancy" is
// an API-side property, not one the client establishes: stopCurrentlyActiveCredits$
// (credit-redemption.effects.ts:188) stops only redemptions already in Active state, so two
// future-dated Pending ones can both go active without the client intervening. Do not sort this
// newest-first to match getRelevantCreditRedeem: publish-link.component.ts reads the returned start
// to time its 30-minute link delay, and the newest would restart that clock on an ad already online
export const getActiveCreditRedeem = (vacancyId: string) =>
  createSelector(getCreditRedeemsFromVacancy(vacancyId), (credits) =>
    credits.find((credit) => CreditRedemptionUtils.isActiveCreditRedeem(credit))
  );

// get all the credits redeem of a vacancy and sort them by start date DESC (latest first)
export const getCreditRedeemsSortedFromLatest = (vacancyId: string) =>
  createSelector(getCreditRedeemsFromVacancy(vacancyId), (credits) =>
    credits ? [...credits].sort(sortByStartDateDesc) : null
  );

// prefer an active redeem, then a future-scheduled one, otherwise the latest by start date
export const getRelevantCreditRedeem = (vacancyId: string) =>
  createSelector(getCreditRedeemsFromVacancy(vacancyId), (credits) =>
    CreditRedemptionUtils.getRelevantRedemption(credits)
  );

export const selectRelevantProductCode = (vacancyId: string) =>
  createSelector(
    getRelevantCreditRedeem(vacancyId),
    (creditRedeem) => creditRedeem?.information.productCode
  );

export const selectIsVacancyDraft = (vacancyId: string) =>
  createSelector(getCreditRedeemsFromVacancy(vacancyId), (credits) => credits.length === 0);

const getCreditRedeemsFromVacancy = (vacancyId: string) =>
  createSelector(selectEntities, (entities) =>
    Object.values(entities).filter((credit) => credit?.vacancyId === vacancyId)
  );

const sortByStartDateDesc = (
  current?: CreditRedemptionViewModel,
  previous?: CreditRedemptionViewModel
): number => (previous?.start?.getTime() ?? 0) - (current?.start?.getTime() ?? 0);
